
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Pencil, Loader2 } from "lucide-react";

interface EditNameDialogProps {
  currentName: string;
  onSave: (newName: string) => Promise<void>;
  title: string;
}

export const EditNameDialog = ({ currentName, onSave, title }: EditNameDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(currentName);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("El nombre no puede estar vacío");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`💾 EditNameDialog: Saving name "${name}" for ${title}`);
      await onSave(name.trim());
      console.log(`✅ EditNameDialog: Name saved successfully, closing modal`);
      setIsOpen(false);
    } catch (error) {
      console.error(`❌ EditNameDialog: Error saving name:`, error);
      setError("No se pudo actualizar el nombre. Inténtalo de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Reset state when opening
      setName(currentName);
      setError(null);
    }
    setIsOpen(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="h-8 w-8 hover:bg-primary/10 sunset:hover:bg-[#FF8000]"
        >
          <Pencil className="h-4 w-4 text-primary dark:text-white sunset:text-white ocean:text-white forest:text-white" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-[#253B3D] border-[#2196A3]/30">
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null); // Clear error when user types
            }}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
            placeholder="Ingresa el nuevo nombre"
            disabled={isLoading}
          />
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}
          <div className="flex gap-2 justify-end">
            <Button 
              variant="outline" 
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isLoading || !name.trim()}
              className="bg-[#2196A3] hover:bg-[#00C1D6] text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
